import { useEffect, useRef, useState, type ReactNode } from 'react';
import { spotlightGradientStyle } from './SpotlightBox';
import { useSpotlight } from '../../hooks/useSpotlight';
import { ACCENT, BORDER, EASE, EASE_SPRING, SURFACE, TEXT_PRIMARY, TEXT_SECONDARY } from '../../tokens';

interface CollapsibleCardProps {
  title: ReactNode;
  children: ReactNode;
  /**
   * Pass with `onToggle` to control the card from a parent — the FAQ list uses
   * this so opening one entry closes the others. Omit both for a card that
   * manages its own open state.
   */
  isOpen?: boolean;
  onToggle?: () => void;
}

/**
 * Expandable card with a spotlight and hover lift.
 *
 * Height animates from a measured `scrollHeight` rather than `auto`, which
 * CSS cannot transition.
 */
export function CollapsibleCard({ title, children, isOpen: controlledOpen, onToggle }: CollapsibleCardProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [height, setHeight] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const { containerRef, spotRef, hovered, handlers } = useSpotlight<HTMLDivElement>();

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const toggle = (): void => {
    if (isControlled) onToggle?.();
    else setUncontrolledOpen((o) => !o);
  };

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    setHeight(isOpen ? el.scrollHeight : 0);
  }, [isOpen, children]);

  const accented = isOpen || hovered;

  return (
    <div
      ref={containerRef}
      {...handlers}
      style={{
        background: SURFACE,
        border: `1px solid ${isOpen ? 'rgba(255,107,0,0.3)' : hovered ? 'rgba(255,107,0,0.25)' : BORDER}`,
        borderLeft: `3px solid ${accented ? ACCENT : 'transparent'}`,
        borderRadius: 16,
        overflow: 'hidden',
        transition: `border-color 0.3s, transform 0.35s ${EASE}, box-shadow 0.35s`,
        transform: hovered ? 'translateY(-5px) scale(1.015)' : 'translateY(0) scale(1)',
        boxShadow: hovered ? '0 16px 48px rgba(255,107,0,0.1)' : 'none',
        position: 'relative',
      }}
    >
      <div ref={spotRef} aria-hidden="true" style={spotlightGradientStyle(hovered)} />

      <button
        type="button"
        aria-expanded={isOpen}
        onClick={toggle}
        style={{
          width: '100%',
          padding: '22px 26px',
          background: 'transparent',
          border: 'none',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          gap: 16,
          textAlign: 'left',
          position: 'relative',
          zIndex: 1,
          font: 'inherit',
        }}
      >
        <span
          style={{
            fontSize: '1rem',
            fontWeight: 600,
            color: accented ? TEXT_PRIMARY : TEXT_SECONDARY,
            fontStyle: 'italic',
            transition: 'color 0.3s',
          }}
        >
          {title}
        </span>
        <span
          aria-hidden="true"
          style={{
            color: ACCENT,
            fontSize: '1.3rem',
            transform: isOpen ? 'rotate(45deg)' : 'rotate(0)',
            transition: `transform 0.35s ${EASE_SPRING}`,
            flexShrink: 0,
          }}
        >
          +
        </span>
      </button>

      <div style={{ height, overflow: 'hidden', transition: `height 0.4s ${EASE}` }}>
        <div
          ref={contentRef}
          style={{
            padding: '0 26px 22px',
            color: TEXT_SECONDARY,
            fontSize: '0.92rem',
            lineHeight: 1.75,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
